import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, X, Cloud, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const PricingPage = () => {
  const [roomCount, setRoomCount] = useState(25);
  const [selectedPlan, setSelectedPlan] = useState('pro');

  const calculatePrice = () => {
    if (selectedPlan === 'starter') {
      return (roomCount * 8).toFixed(2);
    } else if (selectedPlan === 'pro') {
      return (roomCount * 12).toFixed(2);
    }
    return 'Custom';
  };

  return (
    <>
      {/* Header */}
      <section className="py-20 px-6 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl font-bold mb-6">
            Simple, transparent pricing that{' '}
            <span className="bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
              grows with you
            </span>
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Pay only for the rooms you manage. No long-term contracts, no hidden fees, and no surprise charges. Add or remove rooms as your business changes. Cancel anytime.
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-20">
            {/* Starter Plan */}
            <Card className="border-2 hover:border-cyan-200 transition relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-cyan-600 text-white text-sm font-medium rounded-full">
                Most Popular
              </div>
              <CardHeader>
                <CardTitle className="text-2xl">Starter</CardTitle>
                <p className="text-slate-600">Perfect for single-property operations</p>
                <div className="mt-4">
                  <span className="text-4xl font-bold">$8</span>
                  <span className="text-slate-600"> per room/month</span>
                  <p className="text-sm text-slate-500 mt-1">Ideal for 1–20 rooms</p>
                </div>
              </CardHeader>
              <CardContent>
                <Link to="/signup">
                  <Button className="w-full mb-6 bg-gradient-to-r from-cyan-600 to-blue-600">
                    Start 14-day free trial
                  </Button>
                </Link>
                <p className="text-xs text-center text-slate-500 mb-6">No credit card required</p>
                
                <div className="space-y-3">
                  <div className="flex items-start gap-2">
                    <Check className="text-cyan-600 flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-sm">Core PMS (reservations, calendar, check-in/out)</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="text-cyan-600 flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-sm">Housekeeping management with room status tracking</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="text-cyan-600 flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-sm">Maintenance work orders</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="text-cyan-600 flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-sm">Guest database</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="text-cyan-600 flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-sm">Basic reports and occupancy dashboard</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="text-cyan-600 flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-sm">Mobile-friendly interface</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="text-cyan-600 flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-sm">Email support</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pro Plan */}
            <Card className="border-2 border-blue-500 hover:border-blue-600 transition relative shadow-xl">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-medium rounded-full">
                Best Value
              </div>
              <CardHeader>
                <CardTitle className="text-2xl">Pro</CardTitle>
                <p className="text-slate-600">For growing properties and small hotel groups</p>
                <div className="mt-4">
                  <span className="text-4xl font-bold">$12</span>
                  <span className="text-slate-600"> per room/month</span>
                  <p className="text-sm text-slate-500 mt-1">Ideal for 21–100 rooms</p>
                </div>
              </CardHeader>
              <CardContent>
                <Link to="/signup">
                  <Button className="w-full mb-6 bg-gradient-to-r from-blue-600 to-purple-600">
                    Start 14-day free trial
                  </Button>
                </Link>
                <p className="text-xs text-center text-slate-500 mb-6">No credit card required</p>
                
                <p className="font-semibold mb-3 text-sm">Everything in Starter, plus:</p>
                <div className="space-y-3">
                  <div className="flex items-start gap-2">
                    <Check className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-sm">Multi-property management</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-sm">Advanced forecasting (30-60-90 days)</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-sm">Staff scheduling and shift management</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-sm">Audit logs and activity tracking</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-sm">CSV/PDF report exports</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-sm">Rate plans and dynamic pricing</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-sm">Channel manager integration (OTAs)</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-sm">Priority email support</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Enterprise Plan */}
            <Card className="border-2 hover:border-cyan-200 transition">
              <CardHeader>
                <CardTitle className="text-2xl">Enterprise</CardTitle>
                <p className="text-slate-600">For brands and property portfolios</p>
                <div className="mt-4">
                  <span className="text-4xl font-bold">Custom</span>
                  <p className="text-sm text-slate-500 mt-1">100+ rooms across multiple properties</p>
                </div>
              </CardHeader>
              <CardContent>
                <Button variant="outline" className="w-full mb-6">
                  Contact sales
                </Button>
                <p className="text-xs text-center text-slate-500 mb-6">We'll create a custom quote for your needs</p>
                
                <p className="font-semibold mb-3 text-sm">Everything in Pro, plus:</p>
                <div className="space-y-3">
                  <div className="flex items-start gap-2">
                    <Check className="text-purple-600 flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-sm">Unlimited properties and rooms</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="text-purple-600 flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-sm">Dedicated account manager</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="text-purple-600 flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-sm">Custom integrations and priority support</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="text-purple-600 flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-sm">Priority phone and email support</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="text-purple-600 flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-sm">Advanced analytics and business intelligence</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="text-purple-600 flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-sm">White-label options</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="text-purple-600 flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-sm">Custom training and onboarding</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="text-purple-600 flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-sm">SLA guarantees</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Calculator */}
          <Card className="max-w-2xl mx-auto mb-20 border-2 border-cyan-200">
            <CardHeader>
              <CardTitle>Calculate your estimated price</CardTitle>
              <p className="text-slate-600">See what Keyless PMS would cost for your property</p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label htmlFor="rooms">How many rooms does your property have?</Label>
                <Input
                  id="rooms"
                  type="number"
                  value={roomCount}
                  onChange={(e) => setRoomCount(Number(e.target.value))}
                  placeholder="e.g., 25"
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="plan">Which plan are you interested in?</Label>
                <Select value={selectedPlan} onValueChange={setSelectedPlan}>
                  <SelectTrigger id="plan" className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="starter">Starter</SelectItem>
                    <SelectItem value="pro">Pro</SelectItem>
                    <SelectItem value="enterprise">Enterprise (Contact Us)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="p-6 bg-gradient-to-br from-cyan-50 to-blue-50 rounded-lg">
                <p className="text-sm text-slate-600 mb-2">Estimated monthly price:</p>
                <p className="text-4xl font-bold text-cyan-700">
                  {selectedPlan === 'enterprise' ? 'Custom' : `$${calculatePrice()}`}
                  {selectedPlan !== 'enterprise' && <span className="text-xl">/month</span>}
                </p>
                <p className="text-sm text-slate-600 mt-2">
                  For {roomCount} rooms on {selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)} plan
                </p>
              </div>

              <p className="text-sm text-slate-500 text-center">
                These estimates are for illustration. Final pricing is confirmed during signup. Enterprise pricing is customized based on your specific needs.
              </p>

              <p className="text-sm text-center text-slate-600">
                <strong>Remember:</strong> You can invite unlimited users at no extra cost. Pricing is based only on the number of rooms.
              </p>
            </CardContent>
          </Card>

          {/* Comparison Table */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-center mb-12">Compare plans</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2">
                    <th className="text-left p-4 font-bold">Feature</th>
                    <th className="text-center p-4 font-bold">Starter</th>
                    <th className="text-center p-4 font-bold">Pro</th>
                    <th className="text-center p-4 font-bold">Enterprise</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Core PMS (calendar & reservations)', true, true, true],
                    ['Housekeeping & room status', true, true, true],
                    ['Maintenance work orders', true, true, true],
                    ['Guest database', true, true, true],
                    ['Basic reports & dashboard', true, true, true],
                    ['Mobile-friendly interface', true, true, true],
                    ['Multi-property management', false, true, true],
                    ['Advanced forecasting', false, true, true],
                    ['Staff scheduling', false, true, true],
                    ['Audit logs', false, true, true],
                    ['CSV/PDF exports', false, true, true],
                    ['Rate plans & dynamic pricing', false, true, true],
                    ['Channel manager (OTA integration)', false, true, true],
                    ['Advanced analytics & BI', false, false, true],
                    ['Priority support', false, true, true],
                    ['Dedicated account manager', false, false, true],
                    ['Custom integrations', false, false, true],
                    ['White-label options', false, false, true],
                    ['SLA guarantees', false, false, true],
                  ].map(([feature, starter, pro, enterprise], idx) => (
                    <tr key={idx} className="border-b hover:bg-slate-50">
                      <td className="p-4 text-slate-700">{feature}</td>
                      <td className="p-4 text-center">
                        {starter ? <Check className="text-cyan-600 mx-auto" size={20} /> : <X className="text-slate-300 mx-auto" size={20} />}
                      </td>
                      <td className="p-4 text-center">
                        {pro ? <Check className="text-blue-600 mx-auto" size={20} /> : <X className="text-slate-300 mx-auto" size={20} />}
                      </td>
                      <td className="p-4 text-center">
                        {enterprise ? <Check className="text-purple-600 mx-auto" size={20} /> : <X className="text-slate-300 mx-auto" size={20} />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 bg-gradient-to-br from-cyan-600 to-blue-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Start your free trial today</h2>
          <p className="text-xl mb-8 opacity-90">
            No credit card required. Full access to all features. Cancel anytime.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/signup">
              <Button size="lg" className="bg-white text-cyan-700 hover:bg-slate-100 w-full sm:w-auto">
                Start 14-day free trial
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 w-full sm:w-auto">
              Talk to sales
            </Button>
          </div>
          <div className="flex flex-wrap justify-center gap-6 mt-8 text-sm opacity-75">
            <span>✓ 14-day free trial</span>
            <span>✓ No credit card required</span>
            <span>✓ Cancel anytime</span>
            <span>✓ No setup fees</span>
          </div>
        </div>
      </section>
    </>
  );
};

export default PricingPage;
